import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import moment from "moment";

import { atomicConfig } from "../../config";
import { VELOCITY_TOOL_EXTRA_DAYS } from "../../pages/dashboards/StoreManagement/constants";
import {
  emptyAlertsPayload,
  mergeStoreAlerts,
} from "../../utils/adsHealthAlerts";
import { forEachWithConcurrency } from "../../utils/concurrency";
import { call_api_auth } from "../../utils/services";

import { displayErrorWithTimeout } from "./errors";

const initialState = {
  marketplace: null,
  store: null,
  start_date: null,
  end_date: null,
  compare_to_period: null,
  storeHasErrors: false,
  storeAnalyticsData: {},
  storeAnalyticsLoading: false,
  storeAnalyticsErrorMessage: null,
  storeSalesChartData: {},
  storeSalesChartLoading: false,
  storeSalesChartErrorMessage: null,
  storePerformanceData: {},
  storePerformanceLoading: false,
  storePerformanceErrorMessage: null,
  productListData: {},
  productListLoading: false,
  productListErrorMessage: null,
  advancedProductListData: {},
  advancedProductListLoading: false,
  advancedProductListErrorMessage: null,
  storeBusinessReportData: [],
  storeBusinessReportLoading: false,
  storeBusinessReportErrorMessage: null,
  storePlanningsData: {},
  storePlanningsLoading: false,
  storePlanningsErrorMessage: null,
  monthlyGoalsLoading: false,
  monthlyGoalsErrorMessage: null,
  weeklyInsightData: [],
  weeklyInsightLoading: false,
  weeklyInsightErrorMessage: null,
  goalsLoading: false,
  goalsHasErrors: false,
  goals: {},
  goalsErrorMessage: null,
  notificationCountList: {},
  notificationCountLoading: false,
  notificationCountErrorMessage: null,
  storeMonthlyBreakdownMonth: -1,
  storeMonthlyBreakdownData: [],
  storeMonthlyBreakdownLoading: false,
  storeMonthlyBreakdownErrorMessage: null,
  productItemDetailsList: [],
  productItemDetailsLoading: false,
  productItemDetailsErrorMessage: null,
  productSalesList: [],
  productSalesLoading: false,
  productSalesErrorMessage: null,
  // "cache" while the recent-days overlay is still refreshing; "live" once
  // the silent revalidation lands fresh numbers. null before the first response.
  productSalesFreshness: null,
  // Bumped by the Store Analysis refresh button so the table re-fetches even
  // when store/marketplace haven't changed.
  storeAnalysisRefreshKey: 0,
  productRanksList: [],
  productRanksLoading: false,
  productRankssErrorMessage: null,
  productUnitEconomicsList: [],
  productUnitEconomicsLoading: false,
  productUnitEconomicsErrorMessage: null,
  productProfitabilityList: [],
  productProfitabilityLoading: false,
  productProfitabilityErrorMessage: null,
  productProfitabilityYearlyData: {},
  productProfitabilityYearlyLoading: false,
  productProfitabilityYearlyErrorMessage: null,
  productPredictedUnitsList: [],
  productPredictedUnitsLoading: false,
  productPredictedUnitsErrorMessage: null,
  productCommentsData: [],
  productCommentsDataLoading: false,
  productCommentsDataErrorMessage: null,
  ruleEngineDecisionsData: [],
  ruleEngineDecisionsLoading: false,
  ruleEngineDecisionsErrorMessage: null,
  ruleEngineDecisionReasoningData: {},
  ruleEngineDecisionReasoningLoading: false,
  ruleEngineDecisionReasoningErrorMessage: null,
  ruleEngineDecisionTraceData: {},
  ruleEngineDecisionTraceLoading: false,
  ruleEngineDecisionTraceError: null,
  featureDocs: {},
  featureDocsLoading: false,
  featureDocsError: null,
  saveRuleEngineResponseLoading: {},
  saveRuleEngineResponseStatus: {},
  productAsinFeaturesData: {},
  productAsinFeaturesLoading: false,
  productAsinFeaturesErrorMessage: null,
  productBsrData: {},
  productBsrLoading: false,
  productBsrErrorMessage: null,
  productSeasonalityData: {},
  productSeasonalityLoading: false,
  productSeasonalityErrorMessage: null,
  activeInventory: {},
  activeInventoryLoading: false,
  activeInventoryErrorMessage: null,
  predictedUnits: {},
  predictedUnitsList: [],
  predictedUnitsLoading: false,
  predictedUnitsErrorMessage: null,
  sellerForecastReviews: [],
  previousSellerForecastReviews: [],
  sellerForecastReviewsLoading: false,
  sellerForecastReviewsErrorMessage: null,
  saveSellerForecastReviewLoading: false,
  saveSellerForecastReviewStatus: {},
  pastUnitsSales: [],
  pastUnitsSalesLoading: false,
  pastUnitsSalesErrorMessage: null,
  dealUploadStatus: {}, // Track deal upload status per ASIN: { [asin]: { status, batchId, dealAccepted } }
  productDeliveryTimesData: [],
  productDeliveryTimesLoading: false,
  productDeliveryTimesErrorMessage: null,
  ppcAnalysisData: {},
  ppcAnalysisLoading: false,
  ppcAnalysisErrorMessage: null,
  ppcAlertsData: [],
  ppcAlertsLoading: false,
  ppcAlertsErrorMessage: null,
  adsHealthAlerts: {
    generated_at: null,
    summary: { critical: 0, warning: 0, info: 0, dollars_at_risk: 0 },
    alerts: [],
  },
  adsHealthLoading: false,
  adsHealthErrorMessage: null,
  // Set while alerts are fetched store by store: { done, total, failedStores }.
  adsHealthProgress: null,
};

export const getStoreGoals = createAsyncThunk(
  "store/monthlyGoals",
  async (store, { rejectWithValue }) => {
    try {
      if (store === "all" || store === null) {
        return rejectWithValue(err);
      }
      const monthlyGoal = await get_store_monthly_goal(
        store.replace("_client", "")
      );
      return monthlyGoal;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// A slice for recipes with our 3 reducers
const storeAnalyticsSlice = createSlice({
  name: "storeAnalytics",
  initialState,
  reducers: {
    resetAllState: (state) => {
      Object.keys(initialState).forEach((key) => {
        state[key] = initialState[key];
      });
    },
    getStoreAnalytics: (state, { payload }) => {
      state.storeAnalyticsLoading = true;
      state.storeAnalyticsErrorMessage = null;
      state.store = payload.store;
      state.start_date = payload.start_date;
      state.end_date = payload.end_date;
      state.compare_to_period = payload.compare_to_period;
    },
    getStoreAnalyticsSuccess: (state, { payload }) => {
      state.storeAnalyticsData = payload;
      state.storeAnalyticsLoading = false;
      state.storeHasErrors = false;
      state.storeAnalyticsErrorMessage = null;
    },
    getStoreAnalyticsFailure: (state, { payload }) => {
      state.storeAnalyticsLoading = false;
      state.storeHasErrors = true;
      state.storeAnalyticsErrorMessage = payload;
    },
    initStoreAnalytics: (state) => {
      state.storeAnalyticsData = {};
      state.storeAnalyticsLoading = false;
      state.storeHasErrors = false;
      state.storeAnalyticsErrorMessage = null;
      state.start_date = null;
      state.end_date = null;
      state.compare_to_period = null;
    },
    getStorePerformance: (state) => {
      state.storePerformanceLoading = true;
      state.storePerformanceErrorMessage = null;
    },
    getStorePerformanceSuccess: (state, { payload }) => {
      state.storePerformanceData = payload;
      state.storePerformanceLoading = false;
      state.storePerformanceErrorMessage = null;
    },
    getStorePerformanceFailure: (state, { payload }) => {
      state.storePerformanceLoading = false;
      state.storePerformanceErrorMessage = payload;
    },
    getProductList: (state) => {
      state.productListLoading = true;
      state.productListErrorMessage = null;
    },
    getProductListSuccess: (state, { payload }) => {
      state.productListData = payload;
      state.productListLoading = false;
      state.productListErrorMessage = null;
    },
    getProductListFailure: (state, { payload }) => {
      state.productListLoading = false;
      state.productListErrorMessage = payload;
    },
    getAdvancedProductList: (state) => {
      state.advancedProductListLoading = true;
      state.advancedProductListErrorMessage = null;
    },
    getAdvancedProductListSuccess: (state, { payload }) => {
      state.advancedProductListData = payload;
      state.advancedProductListLoading = false;
      state.advancedProductListErrorMessage = null;
    },
    getAdvancedProductListFailure: (state, { payload }) => {
      state.advancedProductListLoading = false;
      state.advancedProductListErrorMessage = payload;
    },
    initAdvancedProductList: (state) => {
      state.advancedProductListData = {};
    },
    getStoreBusinessReport: (state) => {
      state.storeBusinessReportLoading = true;
      state.storeBusinessReportErrorMessage = null;
    },
    getStoreBusinessReportSuccess: (state, { payload }) => {
      state.storeBusinessReportData = payload;
      state.storeBusinessReportLoading = false;
      state.storeBusinessReportErrorMessage = null;
    },
    getStoreBusinessReportFailure: (state, { payload }) => {
      state.storeBusinessReportLoading = false;
      state.storeBusinessReportErrorMessage = payload;
    },
    getStoreSalesChartData: (state, { payload }) => {
      state.storeSalesChartLoading = true;
      state.storeSalesChartErrorMessage = null;
      state.store = payload.store;
      state.start_date = payload.start_date;
      state.end_date = payload.end_date;
      state.compare_to_period = payload.compare_to_period;
    },
    getStoreSalesChartDataSuccess: (state, { payload }) => {
      state.storeSalesChartData = payload;
      state.storeSalesChartLoading = false;
      state.storeSalesChartErrorMessage = null;
    },
    getStoreSalesChartDataFailure: (state, { payload }) => {
      state.storeSalesChartData = {};
      state.storeSalesChartLoading = false;
      state.storeSalesChartErrorMessage = payload;
    },
    getStorePlannings: (state) => {
      state.storePlanningsLoading = true;
      state.storePlanningsErrorMessage = null;
    },
    getStorePlanningsSuccess: (state, { payload }) => {
      state.storePlanningsData = payload;
      state.storePlanningsLoading = false;
      state.storePlanningsErrorMessage = null;
    },
    getStorePlanningsFailure: (state, { payload }) => {
      state.storePlanningsLoading = false;
      state.storePlanningsErrorMessage = payload;
    },
    getStoreMonthlyBreakdown: (state, { payload }) => {
      const newData = [...state.storeMonthlyBreakdownData];
      newData[payload] = [];
      state.storeMonthlyBreakdownData = newData;
      state.storeMonthlyBreakdownMonth = payload;
      state.storeMonthlyBreakdownLoading = true;
      state.storeMonthlyBreakdownErrorMessage = null;
    },
    getStoreMonthlyBreakdownSuccess: (state, { payload }) => {
      const newData = [...state.storeMonthlyBreakdownData];
      newData[payload.month] = payload.data;
      state.storeMonthlyBreakdownData = newData;
      state.storeMonthlyBreakdownMonth = -1;
      state.storeMonthlyBreakdownLoading = false;
      state.storeMonthlyBreakdownErrorMessage = null;
    },
    getStoreMonthlyBreakdownFailure: (state, { payload }) => {
      state.storeMonthlyBreakdownMonth = -1;
      state.storeMonthlyBreakdownLoading = false;
      state.storeMonthlyBreakdownErrorMessage = payload;
    },
    updateMonthlyGoal: (state, { payload }) => {
      state.monthlyGoalsErrorMessage = null;
      const currentGoals = [...state.storePlanningsData.goals];
      const updatedGoalIndex = currentGoals.findIndex(
        (item) =>
          item.month === payload.month &&
          item.year.toString() === payload.year.toString()
      );
      if (updatedGoalIndex !== -1) {
        currentGoals[updatedGoalIndex].goal = payload.goal;
        state.storePlanningsData = {
          ...state.storePlanningsData,
          goals: [...currentGoals],
        };
      }
    },
    updateMonthlyGoalSuccess: (state) => {
      state.monthlyGoalsErrorMessage = null;
    },
    updateMonthlyGoalFailure: (state, { payload }) => {
      state.monthlyGoalsErrorMessage = payload;
    },
    getWeeklyInsight: (state) => {
      state.weeklyInsightLoading = true;
      state.weeklyInsightErrorMessage = null;
    },
    getWeeklyInsightSuccess: (state, { payload }) => {
      state.weeklyInsightData = payload;
      state.weeklyInsightLoading = false;
      state.weeklyInsightErrorMessage = null;
    },
    getWeeklyInsightFailure: (state, { payload }) => {
      state.weeklyInsightData = [];
      state.weeklyInsightLoading = false;
      state.weeklyInsightErrorMessage = payload;
    },
    setStoreSuccess: (state, { payload }) => {
      state.store = payload;
    },
    setMarketplaceSuccess: (state, { payload }) => {
      state.marketplace = payload;
    },
    getNotificationCount: (state) => {
      state.notificationCountList = {};
      state.notificationCountLoading = true;
      state.notificationCountErrorMessage = null;
    },
    getNotificationCountSuccess: (state, { payload }) => {
      state.notificationCountList = payload;
      state.notificationCountLoading = false;
      state.notificationCountErrorMessage = null;
    },
    getNotificationCountFailure: (state, { payload }) => {
      state.notificationCountList = {};
      state.notificationCountLoading = false;
      state.notificationCountErrorMessage = payload;
    },
    getProductItemDetails: (state) => {
      state.productItemDetailsList = [];
      state.productItemDetailsLoading = true;
      state.productItemDetailsErrorMessage = null;
    },
    getProductItemDetailsSuccess: (state, { payload }) => {
      state.productItemDetailsList = payload;
      state.productItemDetailsLoading = false;
      state.productItemDetailsErrorMessage = null;
    },
    getProductItemDetailsFailure: (state, { payload }) => {
      state.productItemDetailsList = [];
      state.productItemDetailsLoading = false;
      state.productItemDetailsErrorMessage = payload;
    },
    getProductSales: (state) => {
      state.productSalesList = [];
      state.productSalesLoading = true;
      state.productSalesErrorMessage = null;
      state.productSalesFreshness = null;
    },
    getProductSalesSuccess: (state, { payload }) => {
      state.productSalesList = payload;
      state.productSalesLoading = false;
      state.productSalesErrorMessage = null;
    },
    getProductSalesFailure: (state, { payload }) => {
      state.productSalesList = [];
      state.productSalesLoading = false;
      state.productSalesErrorMessage = payload;
      state.productSalesFreshness = null;
    },
    setProductSalesFreshness: (state, { payload }) => {
      state.productSalesFreshness = payload;
    },
    bumpStoreAnalysisRefreshKey: (state) => {
      state.storeAnalysisRefreshKey += 1;
      state.productSalesFreshness = null;
    },
    getProductPredictedUnits: (state) => {
      state.productPredictedUnitsList = [];
      state.productPredictedUnitsLoading = true;
      state.productPredictedUnitsErrorMessage = null;
    },
    getProductPredictedUnitsSuccess: (state, { payload }) => {
      state.productPredictedUnitsList = payload.asins;
      state.productPredictedUnitsLoading = false;
      state.productPredictedUnitsErrorMessage = null;
    },
    getProductPredictedUnitsFailure: (state, { payload }) => {
      state.productPredictedUnitsList = [];
      state.productPredictedUnitsLoading = false;
      state.productPredictedUnitsErrorMessage = payload;
    },
    getProductRanks: (state) => {
      state.productRanksList = [];
      state.productRanksLoading = true;
      state.productRanksErrorMessage = null;
    },
    getProductRanksSuccess: (state, { payload }) => {
      state.productRanksList = payload;
      state.productRanksLoading = false;
      state.productRanksErrorMessage = null;
    },
    getProductRanksFailure: (state, { payload }) => {
      state.productRanksList = [];
      state.productRanksLoading = false;
      state.productRanksErrorMessage = payload;
    },
    getProductUnitEconomics: (state) => {
      state.productUnitEconomicsList = [];
      state.productUnitEconomicsLoading = true;
      state.productUnitEconomicsErrorMessage = null;
    },
    getProductUnitEconomicsSuccess: (state, { payload }) => {
      state.productUnitEconomicsList = payload;
      state.productUnitEconomicsLoading = false;
      state.productUnitEconomicsErrorMessage = null;
    },
    getProductUnitEconomicsFailure: (state, { payload }) => {
      state.productUnitEconomicsList = [];
      state.productUnitEconomicsLoading = false;
      state.productUnitEconomicsErrorMessage = payload;
    },
    getProductProfitability: (state) => {
      state.productProfitabilityList = [];
      state.productProfitabilityLoading = true;
      state.productProfitabilityErrorMessage = null;
    },
    getProductProfitabilitySuccess: (state, { payload }) => {
      state.productProfitabilityList = payload;
      state.productProfitabilityLoading = false;
      state.productProfitabilityErrorMessage = null;
    },
    getProductProfitabilityFailure: (state, { payload }) => {
      state.productProfitabilityList = [];
      state.productProfitabilityLoading = false;
      state.productProfitabilityErrorMessage = payload;
    },
    getProductProfitabilityYearly: (state) => {
      state.productProfitabilityYearlyData = {};
      state.productProfitabilityYearlyLoading = true;
      state.productProfitabilityYearlyErrorMessage = null;
    },
    getProductProfitabilityYearlySuccess: (state, { payload }) => {
      state.productProfitabilityYearlyData = payload;
      state.productProfitabilityYearlyLoading = false;
      state.productProfitabilityYearlyErrorMessage = null;
    },
    getProductProfitabilityYearlyFailure: (state, { payload }) => {
      state.productProfitabilityYearlyData = {};
      state.productProfitabilityYearlyLoading = false;
      state.productProfitabilityYearlyErrorMessage = payload;
    },
    getProductLongTermStorageData: (state) => {
      state.productLongTermStorageData = [];
      state.productLongTermStorageDataLoading = true;
      state.productLongTermStorageDataErrorMessage = null;
    },
    getProductLongTermStorageDataSuccess: (state, { payload }) => {
      state.productLongTermStorageData = payload;
      state.productLongTermStorageDataLoading = false;
      state.productLongTermStorageDataErrorMessage = null;
    },
    getProductLongTermStorageDataFailure: (state) => {
      state.productLongTermStorageDataList = [];
      state.productLongTermStorageDataLoading = false;
      state.productLongTermStorageDataErrorMessage = null;
    },
    getProductDeliveryTimes: (state) => {
      state.productDeliveryTimesData = [];
      state.productDeliveryTimesLoading = true;
      state.productDeliveryTimesErrorMessage = null;
    },
    getProductDeliveryTimesSuccess: (state, { payload }) => {
      state.productDeliveryTimesData = payload;
      state.productDeliveryTimesLoading = false;
      state.productDeliveryTimesErrorMessage = null;
    },
    getProductDeliveryTimesFailure: (state, { payload }) => {
      state.productDeliveryTimesData = [];
      state.productDeliveryTimesLoading = false;
      state.productDeliveryTimesErrorMessage = payload;
    },
    getProductComments: (state) => {
      state.productCommentsData = [];
      state.productCommentsDataLoading = true;
      state.productCommentsDataErrorMessage = null;
    },
    getProductCommentsSuccess: (state, { payload }) => {
      state.productCommentsData = payload;
      state.productCommentsDataLoading = false;
      state.productCommentsDataErrorMessage = null;
    },
    getProductCommentsFailure: (state) => {
      state.productCommentsData = [];
      state.productCommentsDataLoading = false;
      state.productCommentsDataErrorMessage = null;
    },
    upsertProductCommentSuccess: (state) => {
      state.productCommentsDataErrorMessage = null;
    },
    upsertProductCommentFailure: (state, { payload }) => {
      state.productCommentsDataErrorMessage = payload;
    },
    deleteProductCommentSuccess: (state) => {
      state.productCommentsDataErrorMessage = null;
    },
    deleteProductCommentFailure: (state, { payload }) => {
      state.productCommentsDataErrorMessage = payload;
    },
    setCommentReminderSuccess: (state, { payload }) => {
      // Update the comment in productCommentsData with the reminder info
      const { comment_id, reminder_time } = payload;
      if (
        state.productCommentsData &&
        Array.isArray(state.productCommentsData)
      ) {
        state.productCommentsData = state.productCommentsData.map((comment) => {
          if (comment.id === comment_id) {
            return { ...comment, reminder_time: reminder_time };
          }
          return comment;
        });
      }
    },
    setCommentReminderFailure: (state, { payload }) => {
      state.productCommentsDataErrorMessage = payload;
    },
    removeCommentReminderSuccess: (state, { payload }) => {
      // Remove reminder from the comment in productCommentsData
      const { comment_id } = payload;
      if (
        state.productCommentsData &&
        Array.isArray(state.productCommentsData)
      ) {
        state.productCommentsData = state.productCommentsData.map((comment) => {
          if (comment.id === comment_id) {
            return { ...comment, reminder_time: null, reminder_date: null };
          }
          return comment;
        });
      }
    },
    removeCommentReminderFailure: (state, { payload }) => {
      state.productCommentsDataErrorMessage = payload;
    },
    getRuleEngineDecisions: (state) => {
      state.ruleEngineDecisionsData = [];
      state.ruleEngineDecisionsLoading = true;
      state.ruleEngineDecisionsErrorMessage = null;
    },
    getRuleEngineDecisionsSuccess: (state, { payload }) => {
      state.ruleEngineDecisionsData = payload;
      state.ruleEngineDecisionsLoading = false;
      state.ruleEngineDecisionsErrorMessage = null;
    },
    getRuleEngineDecisionsFailure: (state, { payload }) => {
      state.ruleEngineDecisionsData = [];
      state.ruleEngineDecisionsLoading = false;
      state.ruleEngineDecisionsErrorMessage = payload;
    },
    saveRuleEngineResponse: (state, { payload }) => {
      const statusKey = payload?.status_key || payload?.execution_id;
      if (statusKey) {
        state.saveRuleEngineResponseLoading[statusKey] = true;
      }
    },
    saveRuleEngineResponseSuccess: (state, { payload }) => {
      const statusKey = payload?.status_key || payload?.execution_id;
      if (statusKey) {
        delete state.saveRuleEngineResponseLoading[statusKey];
      }
      if (statusKey) {
        state.saveRuleEngineResponseStatus[statusKey] = {
          success: true,
          message: payload.message,
          complete: true, // Mark as complete so it won't show from beginning again
        };
      }
    },
    saveRuleEngineResponseFailure: (state, { payload }) => {
      const statusKey = payload?.status_key || payload?.execution_id;
      if (statusKey) {
        delete state.saveRuleEngineResponseLoading[statusKey];
      }
      if (statusKey) {
        state.saveRuleEngineResponseStatus[statusKey] = {
          success: false,
          message: payload.message,
          // Don't mark as complete on failure - user may want to retry
        };
      }
    },
    clearRuleEngineResponseStatus: (state, { payload }) => {
      const statusKey = payload?.status_key || payload?.execution_id;
      if (statusKey && state.saveRuleEngineResponseStatus[statusKey]) {
        // Mark as closed but keep the complete status
        state.saveRuleEngineResponseStatus[statusKey].closed = true;
      }
    },
    getRuleEngineDecisionReasoning: (state) => {
      state.ruleEngineDecisionReasoningData = {};
      state.ruleEngineDecisionReasoningLoading = true;
      state.ruleEngineDecisionReasoningErrorMessage = null;
    },
    getRuleEngineDecisionReasoningSuccess: (state, { payload }) => {
      state.ruleEngineDecisionReasoningData[payload.asin] = payload.data;
      state.ruleEngineDecisionReasoningLoading = false;
      state.ruleEngineDecisionReasoningErrorMessage = null;
    },
    getRuleEngineDecisionReasoningFailure: (state, { payload }) => {
      state.ruleEngineDecisionReasoningData = {};
      state.ruleEngineDecisionReasoningLoading = false;
      state.ruleEngineDecisionReasoningErrorMessage = payload;
    },
    getRuleEngineDecisionTrace: (state) => {
      state.ruleEngineDecisionTraceLoading = true;
      state.ruleEngineDecisionTraceError = null;
    },
    getRuleEngineDecisionTraceSuccess: (state, { payload }) => {
      state.ruleEngineDecisionTraceData[payload.asin] = payload.data;
      state.ruleEngineDecisionTraceLoading = false;
      state.ruleEngineDecisionTraceError = null;
    },
    getRuleEngineDecisionTraceFailure: (state, { payload }) => {
      state.ruleEngineDecisionTraceLoading = false;
      state.ruleEngineDecisionTraceError = payload;
    },
    getFeatureDocs: (state) => {
      state.featureDocsLoading = true;
      state.featureDocsError = null;
    },
    getFeatureDocsSuccess: (state, { payload }) => {
      state.featureDocs = payload;
      state.featureDocsLoading = false;
      state.featureDocsError = null;
    },
    getFeatureDocsFailure: (state, { payload }) => {
      state.featureDocsLoading = false;
      state.featureDocsError = payload;
    },
    getProductAsinFeatures: (state) => {
      state.productAsinFeaturesData = {};
      state.productAsinFeaturesLoading = true;
      state.productAsinFeaturesErrorMessage = null;
    },
    getProductAsinFeaturesSuccess: (state, { payload }) => {
      state.productAsinFeaturesData = payload;
      state.productAsinFeaturesLoading = false;
      state.productAsinFeaturesErrorMessage = null;
    },
    getProductAsinFeaturesFailure: (state, { payload }) => {
      state.productAsinFeaturesData = {};
      state.productAsinFeaturesLoading = false;
      state.productAsinFeaturesErrorMessage = payload;
    },
    getProductBsr: (state) => {
      state.productBsrData = {};
      state.productBsrLoading = true;
      state.productBsrErrorMessage = null;
    },
    getProductBsrSuccess: (state, { payload }) => {
      state.productBsrData = payload;
      state.productBsrLoading = false;
      state.productBsrErrorMessage = null;
    },
    getProductBsrFailure: (state, { payload }) => {
      state.productBsrData = {};
      state.productBsrLoading = false;
      state.productBsrErrorMessage = payload;
    },
    getProductSeasonality: (state) => {
      state.productSeasonalityData = {};
      state.productSeasonalityLoading = true;
      state.productSeasonalityErrorMessage = null;
    },
    getProductSeasonalitySuccess: (state, { payload }) => {
      state.productSeasonalityData = payload;
      state.productSeasonalityLoading = false;
      state.productSeasonalityErrorMessage = null;
    },
    getProductSeasonalityFailure: (state, { payload }) => {
      state.productSeasonalityData = {};
      state.productSeasonalityLoading = false;
      state.productSeasonalityErrorMessage = payload;
    },
    getActiveInventory: (state) => {
      state.activeInventory = {};
      state.activeInventoryLoading = true;
      state.activeInventoryErrorMessage = null;
    },
    getActiveInventorySuccess: (state, { payload }) => {
      state.activeInventory = payload;
      state.activeInventoryLoading = false;
      state.activeInventoryErrorMessage = null;
    },
    getActiveInventoryFailure: (state, { payload }) => {
      state.activeInventory = {};
      state.activeInventoryLoading = false;
      state.activeInventoryErrorMessage = payload;
    },
    getPredictedUnits: (state) => {
      state.predictedUnits = {};
      state.predictedUnitsList = [];
      state.predictedUnitsLoading = true;
      state.predictedUnitsErrorMessage = null;
    },
    getPredictedUnitsSuccess: (state, { payload }) => {
      state.predictedUnits = payload;
      state.predictedUnitsLoading = false;
      state.predictedUnitsErrorMessage = null;
    },
    getPredictedUnitsFailure: (state, { payload }) => {
      state.predictedUnits = {};
      state.predictedUnitsLoading = false;
      state.predictedUnitsErrorMessage = payload;
    },
    getSellerForecastReviews: (state) => {
      state.sellerForecastReviews = [];
      state.previousSellerForecastReviews = [];
      state.sellerForecastReviewsLoading = true;
      state.sellerForecastReviewsErrorMessage = null;
    },
    getSellerForecastReviewsSuccess: (state, { payload }) => {
      if (payload && payload.seller_reviews !== undefined) {
        state.sellerForecastReviews = payload.seller_reviews || [];
        state.previousSellerForecastReviews =
          payload.previous_seller_reviews || [];
      } else {
        state.sellerForecastReviews = payload || [];
        state.previousSellerForecastReviews = [];
      }
      state.sellerForecastReviewsLoading = false;
      state.sellerForecastReviewsErrorMessage = null;
    },
    getSellerForecastReviewsFailure: (state, { payload }) => {
      state.sellerForecastReviews = [];
      state.previousSellerForecastReviews = [];
      state.sellerForecastReviewsLoading = false;
      state.sellerForecastReviewsErrorMessage = payload.message || payload;
    },
    saveSellerForecastReview: (state) => {
      state.saveSellerForecastReviewLoading = true;
    },
    saveSellerForecastReviewSuccess: (state, { payload }) => {
      state.saveSellerForecastReviewLoading = false;
      state.saveSellerForecastReviewStatus[payload.child_asin] = {
        success: true,
        message: payload.message,
      };
    },
    saveSellerForecastReviewFailure: (state, { payload }) => {
      state.saveSellerForecastReviewLoading = false;
      state.saveSellerForecastReviewStatus[payload.child_asin] = {
        success: false,
        message: payload.message,
      };
    },
    updateSellerForecastReviewsLocally: (state, { payload }) => {
      const existingIndex = state.sellerForecastReviews.findIndex(
        (review) =>
          review.asin === payload.child_asin &&
          review.forecast_date === payload.forecast_date
      );

      const newReview = {
        asin: payload.child_asin,
        forecast_date: payload.forecast_date,
        review: payload.review,
        review_date: new Date().toISOString().slice(0, 10),
        original_breakdown: payload.original_breakdown,
        override: payload.override,
        override_total: payload.override_total,
        override_breakdown: payload.override_breakdown,
        override_reason: payload.override_reason,
        override_notes: payload.override_notes,
      };

      if (existingIndex !== -1) {
        state.sellerForecastReviews[existingIndex] = newReview;
      } else {
        state.sellerForecastReviews.push(newReview);
      }
    },
    getPastUnitsSales: (state) => {
      state.pastUnitsSales = [];
      state.pastUnitsSalesLoading = true;
      state.pastUnitsSalesErrorMessage = null;
    },
    getPastUnitsSalesSuccess: (state, { payload }) => {
      state.pastUnitsSales = payload;
      state.pastUnitsSalesLoading = false;
      state.pastUnitsSalesErrorMessage = null;
    },
    getPastUnitsSalesFailure: (state, { payload }) => {
      state.pastUnitsSales = [];
      state.pastUnitsSalesLoading = false;
      state.pastUnitsSalesErrorMessage = payload;
    },
    setDealUploadStatus: (state, { payload }) => {
      const { asin, status, batchId, dealAccepted, sellerCentralUrl } = payload;
      state.dealUploadStatus[asin] = {
        ...state.dealUploadStatus[asin],
        status,
        batchId,
        dealAccepted,
        ...(sellerCentralUrl && { sellerCentralUrl }),
      };
    },
    clearDealUploadStatus: (state, { payload }) => {
      const { asin } = payload;
      delete state.dealUploadStatus[asin];
    },
    getPpcAnalysis: (state) => {
      state.ppcAnalysisData = {};
      state.ppcAnalysisLoading = true;
      state.ppcAnalysisErrorMessage = null;
    },
    getPpcAnalysisSuccess: (state, { payload }) => {
      state.ppcAnalysisData = payload;
      state.ppcAnalysisLoading = false;
      state.ppcAnalysisErrorMessage = null;
    },
    getPpcAnalysisFailure: (state, { payload }) => {
      state.ppcAnalysisData = {};
      state.ppcAnalysisLoading = false;
      state.ppcAnalysisErrorMessage = payload;
    },
    getPpcAlerts: (state) => {
      state.ppcAlertsData = [];
      state.ppcAlertsLoading = true;
      state.ppcAlertsErrorMessage = null;
    },
    getPpcAlertsSuccess: (state, { payload }) => {
      state.ppcAlertsData = payload;
      state.ppcAlertsLoading = false;
      state.ppcAlertsErrorMessage = null;
    },
    getPpcAlertsFailure: (state, { payload }) => {
      state.ppcAlertsData = [];
      state.ppcAlertsLoading = false;
      state.ppcAlertsErrorMessage = payload;
    },
    getAdsHealthAlerts: (state) => {
      state.adsHealthLoading = true;
      state.adsHealthErrorMessage = null;
    },
    getAdsHealthAlertsSuccess: (state, { payload }) => {
      state.adsHealthAlerts = payload;
      state.adsHealthLoading = false;
    },
    getAdsHealthAlertsFailure: (state, { payload }) => {
      state.adsHealthLoading = false;
      state.adsHealthErrorMessage = payload;
    },
    setAdsHealthProgress: (state, { payload }) => {
      state.adsHealthProgress = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getStoreGoals.pending, (state) => {
      state.goalsLoading = true;
    });
    builder.addCase(getStoreGoals.fulfilled, (state, action) => {
      state.goalsLoading = false;
      state.goals = action.payload.data;
    });
    builder.addCase(getStoreGoals.rejected, (state) => {
      state.goalsLoading = false;
      state.goals = {};
    });
  },
});

// Three actions generated from the slice
const {
  resetAllState,
  getStoreAnalytics,
  getStoreAnalyticsSuccess,
  getStoreAnalyticsFailure,
  getStorePerformanceSuccess,
  getProductList,
  getProductListSuccess,
  getProductListFailure,
  getAdvancedProductList,
  getAdvancedProductListSuccess,
  getAdvancedProductListFailure,
  initAdvancedProductList,
  getStoreBusinessReport,
  getStoreBusinessReportSuccess,
  getStoreBusinessReportFailure,
  getStoreSalesChartData,
  getStoreSalesChartDataSuccess,
  getStoreSalesChartDataFailure,
  getStorePlannings,
  getStorePlanningsSuccess,
  getStorePlanningsFailure,
  getStoreMonthlyBreakdown,
  getStoreMonthlyBreakdownSuccess,
  getStoreMonthlyBreakdownFailure,
  updateMonthlyGoal,
  updateMonthlyGoalSuccess,
  updateMonthlyGoalFailure,
  getWeeklyInsight,
  getWeeklyInsightSuccess,
  getWeeklyInsightFailure,
  setStoreSuccess,
  setMarketplaceSuccess,
  getNotificationCount,
  getNotificationCountSuccess,
  getNotificationCountFailure,
  getProductItemDetails,
  getProductItemDetailsSuccess,
  getProductItemDetailsFailure,
  getProductSales,
  getProductSalesSuccess,
  getProductSalesFailure,
  setProductSalesFreshness,
  bumpStoreAnalysisRefreshKey,
  getProductRanks,
  getProductRanksSuccess,
  getProductRanksFailure,
  getProductPredictedUnits,
  getProductPredictedUnitsSuccess,
  getProductPredictedUnitsFailure,
  getProductUnitEconomics,
  getProductUnitEconomicsSuccess,
  getProductUnitEconomicsFailure,
  getProductProfitability,
  getProductProfitabilitySuccess,
  getProductProfitabilityFailure,
  getProductProfitabilityYearly,
  getProductProfitabilityYearlySuccess,
  getProductProfitabilityYearlyFailure,
  getProductLongTermStorageData,
  getProductLongTermStorageDataSuccess,
  getProductLongTermStorageDataFailure,
  getProductDeliveryTimes,
  getProductDeliveryTimesSuccess,
  getProductDeliveryTimesFailure,
  getProductComments,
  getProductCommentsSuccess,
  getProductCommentsFailure,
  upsertProductCommentSuccess,
  upsertProductCommentFailure,
  deleteProductCommentSuccess,
  deleteProductCommentFailure,
  setCommentReminderSuccess,
  setCommentReminderFailure,
  removeCommentReminderSuccess,
  removeCommentReminderFailure,
  getRuleEngineDecisions,
  getRuleEngineDecisionsSuccess,
  getRuleEngineDecisionsFailure,
  getRuleEngineDecisionReasoning,
  getRuleEngineDecisionReasoningSuccess,
  getRuleEngineDecisionReasoningFailure,
  getRuleEngineDecisionTrace,
  getRuleEngineDecisionTraceSuccess,
  getRuleEngineDecisionTraceFailure,
  getFeatureDocs,
  getFeatureDocsSuccess,
  getFeatureDocsFailure,
  saveRuleEngineResponse,
  saveRuleEngineResponseSuccess,
  saveRuleEngineResponseFailure,
  clearRuleEngineResponseStatus,
  getProductAsinFeatures,
  getProductAsinFeaturesSuccess,
  getProductAsinFeaturesFailure,
  getProductBsr,
  getProductBsrSuccess,
  getProductBsrFailure,
  getProductSeasonality,
  getProductSeasonalitySuccess,
  getProductSeasonalityFailure,
  getActiveInventory,
  getActiveInventorySuccess,
  getActiveInventoryFailure,
  getPredictedUnits,
  getPredictedUnitsSuccess,
  getPredictedUnitsFailure,
  getSellerForecastReviews,
  getSellerForecastReviewsSuccess,
  getSellerForecastReviewsFailure,
  saveSellerForecastReview,
  saveSellerForecastReviewSuccess,
  saveSellerForecastReviewFailure,
  updateSellerForecastReviewsLocally,
  getPastUnitsSales,
  getPastUnitsSalesSuccess,
  getPastUnitsSalesFailure,
  setDealUploadStatus,
  getPpcAnalysis,
  getPpcAnalysisSuccess,
  getPpcAnalysisFailure,
  getPpcAlerts,
  getPpcAlertsSuccess,
  getPpcAlertsFailure,
  getAdsHealthAlerts,
  getAdsHealthAlertsSuccess,
  getAdsHealthAlertsFailure,
  setAdsHealthProgress,
} = storeAnalyticsSlice.actions;

// A selector
export const storeAnalyticsSelector = (state) => state.store_analytics;

// The reducer
export default storeAnalyticsSlice.reducer;

export function deleteProductComment(
  store,
  comment_id,
  start_date,
  end_date,
  marketplace
) {
  return async (dispatch) => {
    const storeCode = String(store ?? "").replace("_client", "");
    let url = `${atomicConfig.storeManagementServiceUrl}/store-analysis-comment/${storeCode}?comment_id=${comment_id}&marketplace=${marketplace}`;
    try {
      let result = await call_api_auth(url, "DELETE");
      result = result.data;
      if (result) {
        dispatch(deleteProductCommentSuccess());
        dispatch(
          fetchComments({
            store: store,
            start_date: start_date,
            end_date: end_date,
            marketplace: marketplace,
          })
        );
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(deleteProductCommentFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function upsertProductComment(
  payload,
  store,
  start_date,
  end_date,
  marketplace
) {
  return async (dispatch) => {
    const storeCode = String(store ?? "").replace("_client", "");
    let url = `${atomicConfig.storeManagementServiceUrl}/upsert-store-analysis-comment/${storeCode}`;
    let json_body = JSON.stringify({
      ...payload,
      marketplace: marketplace,
    });

    try {
      let result = await call_api_auth(url, "POST", json_body);
      result = result.data;
      if (result) {
        await dispatch(
          fetchComments({
            store: store,
            start_date: start_date,
            end_date: end_date,
            marketplace: marketplace,
          })
        );
        dispatch(upsertProductCommentSuccess(payload));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(upsertProductCommentFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function setCommentReminder(payload, store, start_date, end_date) {
  return async (dispatch) => {
    const storeCode = String(store ?? "").replace("_client", "");
    let url = `${atomicConfig.storeManagementServiceUrl}/set-comment-reminder/${storeCode}`;
    let json_body = JSON.stringify(payload);

    try {
      let result = await call_api_auth(url, "POST", json_body);
      result = result.data;
      if (result) {
        dispatch(setCommentReminderSuccess(payload));
        dispatch(
          fetchComments({
            store: store,
            start_date: start_date,
            end_date: end_date,
          })
        );
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(setCommentReminderFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function removeCommentReminder(payload, store, start_date, end_date) {
  return async (dispatch) => {
    const storeCode = String(store ?? "").replace("_client", "");
    let url = `${atomicConfig.storeManagementServiceUrl}/remove-comment-reminder/${storeCode}`;
    let json_body = JSON.stringify(payload);

    try {
      let result = await call_api_auth(url, "POST", json_body);
      result = result.data;
      if (result) {
        dispatch(removeCommentReminderSuccess(payload));
        dispatch(
          fetchComments({
            store: store,
            start_date: start_date,
            end_date: end_date,
          })
        );
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(removeCommentReminderFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

// Asynchronous thunk action
function _fetchStoreAnalytics(payload) {
  return async (dispatch) => {
    dispatch(getStoreAnalytics(payload));

    let url = `${atomicConfig.storeManagementServiceUrl}/analytics/${payload.store}?start_date=${payload.start_date}&end_date=${payload.end_date}&compare_to_period=${payload.compare_to_period}`;
    try {
      let result = await call_api_auth(url, "GET");

      // let result = brand_data; // for local testing
      result = result.data;

      if (result.message !== undefined) {
        dispatch(getStoreAnalyticsFailure(result.message));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.message,
          })
        );
      } else {
        dispatch(getStoreAnalyticsSuccess(result));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getStoreAnalyticsFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function resetAnalyticsState() {
  return async (dispatch) => {
    dispatch(resetAllState());
  };
}

export function fetchProductItemDetails(payload) {
  return async (dispatch, getState) => {
    dispatch(getProductItemDetails());
    const { store, marketplace, level } = payload;

    let url = `${atomicConfig.storeManagementServiceUrl}/fetch-product-item-details/${store}?level=${level}&marketplace=${marketplace}&use_new_hierarchy=true`;

    try {
      let result = await call_api_auth(url, "GET");
      if (getState().store_analytics.marketplace !== marketplace) return;
      if (result?.error) {
        dispatch(getProductItemDetailsFailure(result.error));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result?.error,
          })
        );
      } else {
        dispatch(getProductItemDetailsSuccess(result.data.product_item[0]));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getProductItemDetailsFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

// The backend no longer duplicates each term's historical search-volume series
// onto every (asin, term) entry. Instead it sends a single top-level
// `_search_volumes` map keyed by term (only terms that survive the MIN_SV
// filter are present). Re-attach each series (by reference, so no data is
// duplicated) onto the per-term entries and drop `_search_volumes`, so the
// store shape stays identical to what all downstream consumers already expect.
// Terms without a series are left without `historical`, matching the legacy
// behavior where absent history falls back to the plain breakdown tooltip.
function rehydrateProductRanks(data) {
  if (!data || typeof data !== "object") {
    return data;
  }

  const searchVolumes = data._search_volumes || {};

  Object.entries(data).forEach(([asin, terms]) => {
    if (asin === "_search_volumes" || !terms || typeof terms !== "object") {
      return;
    }
    Object.entries(terms).forEach(([term, termData]) => {
      if (termData && typeof termData === "object" && searchVolumes[term]) {
        termData.historical = searchVolumes[term];
      }
    });
  });

  delete data._search_volumes;
  return data;
}

// fetch-products-ranks is the slowest endpoint on the page — a duplicate
// dispatch while one is already pending must not fire a second request.
const productRanksInFlight = new Set();

export function fetchProductRanks(payload) {
  return async (dispatch, getState) => {
    const { store, marketplace, start_date, end_date } = payload;
    let url = `${atomicConfig.storeManagementServiceUrl}/fetch-products-ranks/${store}?start_date=${start_date}&end_date=${end_date}&marketplace=${marketplace}&marketplace=${marketplace}`;

    if (productRanksInFlight.has(url)) return; // identical request already pending
    productRanksInFlight.add(url);

    dispatch(getProductRanks());

    try {
      let result = await call_api_auth(url, "GET");
      if (getState().store_analytics.marketplace !== marketplace) return;
      if (result?.error) {
        dispatch(getProductRanksFailure(result.error));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result?.error,
          })
        );
      } else {
        dispatch(getProductRanksSuccess(rehydrateProductRanks(result.data)));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getProductRanksFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    } finally {
      productRanksInFlight.delete(url);
    }
  };
}

// Tracks the most recent (non-silent) product-sales request so a delayed
// silent revalidation can't overwrite state after the user switched
// store/date-range/marketplace.
let latestProductSalesUrl = null;

// Revalidation strategy: when the backend signals it served stale
// recent-sales data (X-Recent-Sales-Refreshing), re-request IMMEDIATELY with
// wait_for_fresh=true — the backend holds that response (bounded server-side)
// while its background refresh lands and returns whatever has landed by then,
// so fresh data arrives in one round trip with no client polling interval.
// Further rounds cover catalogs too large to refresh within one hold: the
// refresh writes its progress as it goes and chains follow-up runs on its
// own, and each round renders what has landed so far. Every round is a full
// fetch-products-sales call, so the pause between rounds backs off (2s, 4s,
// 8s, 16s, then 30s) and the budget below spans roughly four minutes of wall
// time on eight calls — enough for a store like mainstream, whose refresh
// needs two or three 60s runs (SP-API lands ~60 ASINs in a burst, then about
// one every two seconds), where four rounds two seconds apart ended after
// ~70s with the label stuck on "cache" until the next page load.
const PRODUCT_SALES_REVALIDATE_MAX_ATTEMPTS = 8;
const PRODUCT_SALES_REVALIDATE_RETRY_DELAY_MS = 2000;
const PRODUCT_SALES_REVALIDATE_MAX_DELAY_MS = 30000;

// Pause before the round after `attempt`: none after the initial (stale)
// response — the server holds that first revalidation — then doubling from
// the base delay up to the cap.
const revalidatePauseMs = (attempt) =>
  attempt === 0
    ? 0
    : Math.min(
        PRODUCT_SALES_REVALIDATE_MAX_DELAY_MS,
        PRODUCT_SALES_REVALIDATE_RETRY_DELAY_MS * 2 ** (attempt - 1)
      );

// Duplicate (non-silent) dispatches while an identical request is pending must
// not fire a second network call. Silent revalidations bypass this: they run
// sequentially by construction and must never be starved.
const productSalesInFlight = new Set();

export function fetchProductSales(
  payload,
  { silent = false, attempt = 0 } = {}
) {
  return async (dispatch, getState) => {
    const { store, marketplace, level, start_date, end_date } = payload;
    const baseUrl = `${atomicConfig.storeManagementServiceUrl}/fetch-products-sales/${store}?level=${level}&start_date=${start_date}&end_date=${end_date}&marketplace=${marketplace}`;
    const url = silent ? `${baseUrl}&wait_for_fresh=true` : baseUrl;

    if (!silent) {
      if (productSalesInFlight.has(baseUrl)) return; // identical request already pending
      productSalesInFlight.add(baseUrl);
      latestProductSalesUrl = baseUrl;
      dispatch(getProductSales());
    }

    // Next revalidation round, while attempts remain and the user is still
    // on this selection. After a stale initial response it goes right away
    // (the server holds it while the refresh lands); after a silent round —
    // hold expired mid-refresh, or the call failed — a backed-off pause first.
    const revalidate = () => {
      if (attempt >= PRODUCT_SALES_REVALIDATE_MAX_ATTEMPTS) return;
      const next = () => {
        if (latestProductSalesUrl !== baseUrl) return; // selection changed meanwhile
        dispatch(
          fetchProductSales(payload, {
            silent: true,
            attempt: attempt + 1,
          })
        );
      };
      const pause = revalidatePauseMs(attempt);
      if (pause > 0) {
        setTimeout(next, pause);
      } else {
        next();
      }
    };

    try {
      let result = await call_api_auth(url, "GET");
      if (getState().store_analytics.marketplace !== marketplace) return;
      if (silent && latestProductSalesUrl !== baseUrl) return; // selection changed meanwhile
      if (result?.error) {
        if (silent) {
          // Stale data is already rendered — no error surfaced, but a failed
          // round must not end the cycle while attempts remain.
          revalidate();
          return;
        }
        dispatch(getProductSalesFailure(result.error));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result?.error,
          })
        );
      } else {
        const stillRefreshing =
          result?.headers?.["x-recent-sales-refreshing"] === "true";
        // Render every response, a still-refreshing revalidation included:
        // the backend returns whatever its refresh has landed so far (the
        // cache is written progressively), so for a catalog too large to
        // refresh within one hold this body is fresher than what is on
        // screen — not a copy of it. Skipping it left the table stuck on the
        // first response for exactly the stores that needed the refresh.
        dispatch(getProductSalesSuccess(result.data));
        // Drive the cache/live label on the recent date columns. Stay on
        // "cache" while the overlay is still refreshing; flip to "live" once
        // a clean response lands (including the first response when the
        // backend already had fresh data).
        dispatch(setProductSalesFreshness(stillRefreshing ? "cache" : "live"));
        if (stillRefreshing) revalidate();
      }
    } catch (error) {
      if (silent) {
        // A dropped hold (gateway timeout, network) must not end the cycle.
        revalidate();
        return;
      }
      const message = error.message || "Something went wrong";
      dispatch(getProductSalesFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    } finally {
      if (!silent) productSalesInFlight.delete(baseUrl);
    }
  };
}

export function fetchComments(payload) {
  return async (dispatch, getState) => {
    dispatch(getProductComments());

    const { store, marketplace, start_date, end_date } = payload;
    let url = `${atomicConfig.storeManagementServiceUrl}/store-analysis-comments/${store}?start_date=${start_date}&end_date=${end_date}&marketplace=${marketplace}`;

    try {
      let result = await call_api_auth(url, "GET");
      if (getState().store_analytics.marketplace !== marketplace) return;
      if (result?.error) {
        dispatch(getProductCommentsFailure(result.error));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result?.error,
          })
        );
      } else {
        dispatch(getProductCommentsSuccess(result.data));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getProductCommentsFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

// The velocity tool should only send whole numbers. Null targets are treated
// as 0, but warned about so backend gaps don't go unnoticed.
const replaceNullTargets = (asins, store) => {
  const normalized = {};
  Object.entries(asins || {}).forEach(([asin, entry]) => {
    if (!entry) return;
    const fixed = { ...entry };
    ["original_target", "adj_target"].forEach((field) => {
      if (!Array.isArray(entry[field])) return;
      const nullDates = [];
      fixed[field] = entry[field].map((value, i) => {
        if (value === null || value === undefined) {
          nullDates.push(entry.dates?.[i] ?? `index ${i}`);
          return 0;
        }
        return value;
      });
      if (nullDates.length > 0) {
        console.warn(
          `velocity-tool-sales (${store}): ${asin} has null ${field} on ${nullDates.join(
            ", "
          )}; using 0.`
        );
      }
    });
    normalized[asin] = fixed;
  });
  return normalized;
};

export function fetchProductPredictedUnits(payload) {
  return async (dispatch, getState) => {
    dispatch(getProductPredictedUnits());

    const { store, marketplace, start_date } = payload;
    // Ask for targets up to VELOCITY_TOOL_EXTRA_DAYS past today so the table
    // can show the upcoming days.
    const end_date = moment()
      .add(VELOCITY_TOOL_EXTRA_DAYS, "days")
      .format("YYYY-MM-DD");
    let url = `${atomicConfig.storeManagementServiceUrl}/velocity-tool-sales/${store}?start_date=${start_date}&end_date=${end_date}&marketplace=${marketplace}`;

    try {
      let result = await call_api_auth(url, "GET");
      if (getState().store_analytics.marketplace !== marketplace) return;
      if (result?.error) {
        dispatch(getProductPredictedUnitsFailure(result.error));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result?.error,
          })
        );
      } else {
        dispatch(
          getProductPredictedUnitsSuccess({
            ...result.data,
            asins: replaceNullTargets(result.data?.asins, store),
          })
        );
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getProductPredictedUnitsFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function fetchProductProfitability(payload) {
  return async (dispatch, getState) => {
    dispatch(getProductProfitability());

    const { store, marketplace, start_date, end_date } = payload;
    let url = `${atomicConfig.storeManagementServiceUrl}/fetch-products-profitability/${store}?start_date=${start_date}&end_date=${end_date}&marketplace=${marketplace}`;

    try {
      let result = await call_api_auth(url, "GET");
      if (getState().store_analytics.marketplace !== marketplace) return;
      if (result?.error) {
        dispatch(getProductProfitabilityFailure(result.error));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result?.error,
          })
        );
      } else {
        dispatch(getProductProfitabilitySuccess(result.data));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getProductProfitabilityFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function fetchProductProfitabilityYearly(payload) {
  return async (dispatch, getState) => {
    dispatch(getProductProfitabilityYearly());

    const { store, marketplace, year } = payload;
    let url = `${atomicConfig.storeManagementServiceUrl}/fetch-products-profitability-yearly/${store}?year=${year}&marketplace=${marketplace}`;

    try {
      let result = await call_api_auth(url, "GET");
      if (getState().store_analytics.marketplace !== marketplace) return;
      if (result?.error) {
        dispatch(getProductProfitabilityYearlyFailure(result.error));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result?.error,
          })
        );
      } else {
        dispatch(getProductProfitabilityYearlySuccess(result.data));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getProductProfitabilityYearlyFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function fetchProductAsinFeatures(payload) {
  return async (dispatch, getState) => {
    dispatch(getProductAsinFeatures());

    const { store, marketplace } = payload;
    let url = `${atomicConfig.storeManagementServiceUrl}/asin-features/${store}?marketplace=${marketplace}`;

    try {
      let result = await call_api_auth(url, "GET");
      if (getState().store_analytics.marketplace !== marketplace) return;
      if (result?.error) {
        dispatch(getProductAsinFeaturesFailure(result.error));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result?.error,
          })
        );
      } else {
        dispatch(getProductAsinFeaturesSuccess(result.data));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getProductAsinFeaturesFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

// Weekly seasonality index per ASIN, the whole published 52-week cycle — the
// chart shown when the product table's weighted-rank row label is hovered.
// Response: { run_month, week_ends: [52 Saturdays], asins: { [asin]:
// { basis, weight_mode, n_queries, coverage, purchases_si: [52 values] } } }.
// A failure here only costs a tooltip, so it is recorded in state but not
// surfaced as a page-level error banner.
export function fetchProductSeasonality(payload) {
  return async (dispatch, getState) => {
    dispatch(getProductSeasonality());

    const { store, marketplace } = payload;
    const url = `${atomicConfig.storeManagementServiceUrl}/weekly-seasonality-index/${store}?marketplace=${marketplace}`;

    try {
      const result = await call_api_auth(url, "GET");
      if (getState().store_analytics.marketplace !== marketplace) return;
      if (result?.error) {
        dispatch(getProductSeasonalityFailure(result.error));
      } else {
        dispatch(getProductSeasonalitySuccess(result.data));
      }
    } catch (error) {
      dispatch(
        getProductSeasonalityFailure(error.message || "Something went wrong")
      );
    }
  };
}

// Fetch BSR (Best Seller Rank) data for products
// Expected response format per ASIN:
// { categories: [{catId: number, name: string}, ...], ranks: { [catId]: [{time: string, salesRank: number}, ...] } }
export function fetchProductBsr(payload) {
  return async (dispatch, getState) => {
    dispatch(getProductBsr());

    const { store, start_date, end_date, marketplace } = payload;
    let url = `${atomicConfig.storeManagementServiceUrl}/fetch-products-bsr/${store}?start_date=${start_date}&end_date=${end_date}&marketplace=${marketplace}`;

    try {
      let result = await call_api_auth(url, "GET");
      if (getState().store_analytics.marketplace !== marketplace) return;
      if (result?.error) {
        dispatch(getProductBsrFailure(result.error));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result?.error,
          })
        );
      } else {
        dispatch(getProductBsrSuccess(result.data));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getProductBsrFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function fetchProductUnitEconomics(payload) {
  return async (dispatch, getState) => {
    dispatch(getProductUnitEconomics());

    const { store, marketplace, level, start_date, end_date } = payload;
    let url = `${atomicConfig.storeManagementServiceUrl}/fetch-products-unit-economics/${store}?level=${level}&start_date=${start_date}&end_date=${end_date}&marketplace=${marketplace}`;

    try {
      let result = await call_api_auth(url, "GET");
      if (getState().store_analytics.marketplace !== marketplace) return;
      if (result?.error) {
        dispatch(getProductUnitEconomicsFailure(result.error));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result?.error,
          })
        );
      } else {
        dispatch(getProductUnitEconomicsSuccess(result.data));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getProductUnitEconomicsFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function fetchProductsLongTermStorageData(payload) {
  return async (dispatch, getState) => {
    dispatch(getProductLongTermStorageData());

    const { store, marketplace } = payload;
    let url = `${atomicConfig.storeManagementServiceUrl}/long-term-storage/${store}?marketplace=${marketplace}`;

    try {
      let result = await call_api_auth(url, "GET");
      if (getState().store_analytics.marketplace !== marketplace) return;
      if (result?.error) {
        dispatch(getProductLongTermStorageDataFailure(result.error));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.error,
          })
        );
      } else {
        dispatch(getProductLongTermStorageDataSuccess(result.data));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getProductLongTermStorageDataFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function fetchProductDeliveryTimes(payload) {
  return async (dispatch) => {
    dispatch(getProductDeliveryTimes());

    const { store, marketplace, is_client } = payload;
    let url = `${atomicConfig.storeManagementServiceUrl}/fetch-products-delivery-times/${store}?marketplace=${marketplace}&is_client=${is_client}`;

    try {
      let result = await call_api_auth(url, "GET");
      if (result?.error) {
        dispatch(getProductDeliveryTimesFailure(result.error));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.error,
          })
        );
      } else {
        dispatch(
          getProductDeliveryTimesSuccess(result.data?.delivery_times || [])
        );
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getProductDeliveryTimesFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

// Asynchronous thunk action
export function fetchProductList(payload) {
  return async (dispatch, getState) => {
    if (payload.product) {
      dispatch(getAdvancedProductList(payload));
    } else {
      dispatch(getProductList(payload));
    }

    const date = new Date();
    const three_days_before = date.setDate(date.getDate() - 3);
    const converted_date = new Date(three_days_before);
    var end_date = moment(converted_date);
    var start_date = end_date.clone().subtract(payload.duration - 1, "d");
    if (payload.duration) {
      start_date = moment(payload.duration.start_date);
      end_date = moment(payload.duration.end_date);
    }

    var compare_end_date =
      payload.compare === "last_period"
        ? start_date.clone().subtract(1, "d")
        : end_date.clone().subtract(1, "y");
    var compare_start_date =
      payload.compare === "last_period"
        ? start_date.clone().subtract(payload.duration, "d")
        : start_date.clone().subtract(1, "y");
    if (
      payload.compare &&
      payload.compare.start_date &&
      payload.compare.end_date
    ) {
      compare_start_date = moment(payload.compare.start_date);
      compare_end_date = moment(payload.compare.end_date);
    }

    let results = {};
    // The store is a path segment so the authorizer can scope access to it by
    // ARN; callers pass the store-picker identifier, which keeps a "_client"
    // suffix the backend cannot resolve.
    const store = String(payload.store ?? "").replace("_client", "");
    let url = `${atomicConfig.storeManagementServiceUrl}/product-list/${store}`;

    const json_body = JSON.stringify({
      current: {
        start_date: start_date.format("YYYY-MM-DD"),
        end_date: end_date.format("YYYY-MM-DD"),
      },
      compare: {
        start_date: compare_start_date.format("YYYY-MM-DD"),
        end_date: compare_end_date.format("YYYY-MM-DD"),
      },
      ...(payload.product ? { product: payload.product } : {}),
      ...(payload.hierarchy ? { hierarchy: "hierarchy" } : {}),
      ...(payload.marketplace
        ? { marketplace: payload.marketplace }
        : { marketplace: "us" }),
    });

    try {
      let result = await call_api_auth(url, "POST", json_body);
      const effectiveMarketplace = payload.marketplace || "us";
      if (getState().store_analytics.marketplace !== effectiveMarketplace)
        return;

      result = result.data;

      if (result.message !== undefined) {
        if (payload.product) {
          dispatch(getAdvancedProductListFailure(result.message));
        } else {
          dispatch(getProductListFailure(result.message));
        }

        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.message,
          })
        );
      } else {
        Object.keys(result).forEach((key) => {
          result[key]["store"] = payload.store;
        });
        results = Object.assign({}, results, result);

        if (payload.product) {
          dispatch(getAdvancedProductListSuccess(results));
        } else {
          dispatch(getProductListSuccess(results));
        }
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      if (payload.product) {
        dispatch(getAdvancedProductListFailure(message));
      } else {
        dispatch(getProductListFailure(message));
      }
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function resetAdvancedProductList() {
  return async (dispatch) => {
    dispatch(initAdvancedProductList());
  };
}

function _fetchStoreBusinessReport(payload) {
  return async (dispatch) => {
    dispatch(getStoreBusinessReport(payload));

    let url = `${atomicConfig.storeManagementServiceUrl}/business-report/${payload.store}?start_date=${payload.start_date}&end_date=${payload.end_date}`;
    try {
      let result = await call_api_auth(url, "GET");
      // let result = brand_data; // for local testing
      result = result.data;

      if (result.message !== undefined) {
        dispatch(getStoreBusinessReportFailure(result.message));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.message,
          })
        );
      } else {
        dispatch(getStoreBusinessReportSuccess(result));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getStoreBusinessReportFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function fetchPerformanceData(payload = {}) {
  return async (dispatch) => {
    dispatch(getStorePerformanceSuccess(payload));
  };
}
export function clearStoreSalesChartData() {
  return async (dispatch) => {
    dispatch(getStoreSalesChartDataFailure(""));
  };
}

export function fetchStoreSalesChartData(payload) {
  return async (dispatch) => {
    dispatch(getStoreSalesChartData(payload));

    if (payload.store === "all" || payload.store === null) {
      dispatch(getStoreSalesChartDataFailure("No store selected"));
      return;
    }

    let url = `${atomicConfig.storeManagementServiceUrl}/sales-chart-data/${payload.store}?start_date=${payload.start_date}&end_date=${payload.end_date}`;
    if (
      typeof payload.compare_start_date != "undefined" &&
      typeof payload.compare_end_date != "undefined"
    ) {
      url += `&compare_start_date=${payload.compare_start_date}&compare_end_date=${payload.compare_end_date}`;
    }
    try {
      let result = await call_api_auth(url, "GET");
      result = result.data;

      if (result.message !== undefined) {
        dispatch(getStoreSalesChartDataFailure(result.message));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.message,
          })
        );
      } else {
        dispatch(getStoreSalesChartDataSuccess(result));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getStoreSalesChartDataFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

// Enough for an "All stores" view to go out in one wave: the budget portfolio
// is nine stores and a client's grants are fewer. The cap only bites on the
// cookie path, where an internal user's grants can run to the whole fleet, and
// it is no heavier than the cross-store route it replaces, which fanned out
// over every store server-side with an unbounded thread pool.
const GOALS_FETCH_CONCURRENCY = 20;

const SUMMED_GOAL_FIELDS = [
  "goal",
  "profit_goal",
  "actual_extra_profit",
  "last_year_same_month",
  "sales_to_date",
];

const round2 = (value) => Math.round(value * 100) / 100;

// One retry, because fanning out multiplies the chance of meeting a blip: the
// single cross-store request this replaces had one opportunity to fail, and
// twenty requests have twenty. Only for failures that a second attempt could
// plausibly clear.
const GOALS_FETCH_RETRIES = 1;
const GOALS_RETRY_BACKOFF_MS = 300;

async function withRetry(attemptOnce) {
  for (let attempt = 0; ; attempt++) {
    try {
      return await attemptOnce();
    } catch (error) {
      if (attempt >= GOALS_FETCH_RETRIES || !error.transient) throw error;
      await new Promise((resolve) =>
        setTimeout(resolve, GOALS_RETRY_BACKOFF_MS * (attempt + 1))
      );
    }
  }
}

async function fetchPerStore(stores, fetchOne) {
  const results = new Array(stores.length);
  let next = 0;
  const worker = async () => {
    while (next < stores.length) {
      const i = next++;
      results[i] = await withRetry(() => fetchOne(stores[i]));
    }
  };
  await Promise.all(
    Array.from(
      { length: Math.min(GOALS_FETCH_CONCURRENCY, stores.length) },
      worker
    )
  );
  return results;
}

async function getGoalsData(url) {
  let response;
  try {
    response = await call_api_auth(url, "GET");
  } catch (error) {
    // A dropped connection or a 5xx may clear on a second attempt. A 4xx says
    // the request itself is wrong, and repeating it changes nothing.
    error.transient = !error.response || error.response.status >= 500;
    throw error;
  }
  if (!response) {
    // The legacy path returns nothing once the token has expired, having
    // already started redirecting to sign-in.
    throw Object.assign(new Error("Session expired"), { transient: false });
  }
  const result = response.data;
  if (result.message !== undefined) {
    throw Object.assign(new Error(result.message), { transient: false });
  }
  return result;
}

// The backend sends profit_to_date as "" when no cached profit exists for the
// month. Keep "" only while every store is missing it, so a partial total is
// never shown as zero profit.
const addProfit = (total, value) => {
  if (value === "" || value === null || value === undefined) return total;
  return (total === "" ? 0 : total) + value;
};

function sumMonthlyGoals(responses, year) {
  const byMonth = new Map();
  responses.forEach(({ goals }) => {
    goals.forEach((row) => {
      const total = byMonth.get(row.month_);
      if (!total) {
        byMonth.set(row.month_, {
          ...row,
          store: "all",
          profit_to_date: addProfit("", row.profit_to_date),
        });
        return;
      }
      SUMMED_GOAL_FIELDS.forEach((field) => {
        total[field] = (total[field] || 0) + (row[field] || 0);
      });
      total.profit_to_date = addProfit(
        total.profit_to_date,
        row.profit_to_date
      );
    });
  });

  const goals = [...byMonth.values()].sort((a, b) => a.month_ - b.month_);
  goals.forEach((row) => {
    SUMMED_GOAL_FIELDS.forEach((field) => {
      row[field] = round2(row[field] || 0);
    });
    if (row.profit_to_date !== "")
      row.profit_to_date = round2(row.profit_to_date);
    // A ratio of totals, never an average of the per-store percentages.
    row.actual_vs_goal =
      row.sales_to_date && row.goal
        ? round2((row.sales_to_date / row.goal) * 100)
        : 0;
  });
  return { year: responses[0]?.year ?? year, store: "all", goals };
}

const storesToFetch = (payload) =>
  payload.store && payload.store !== "all"
    ? [payload.store]
    : payload.stores || [];

export function fetchStoreMonthlyBreakdown(payload) {
  return async (dispatch) => {
    dispatch(getStoreMonthlyBreakdown(payload.month));

    // One request per store, with the store as a path segment so the
    // authorizer enforces it. The rows are one per store, so combining them
    // is plain concatenation.
    const base = `${atomicConfig.storeManagementServiceUrl}/fetch-breakdown-goals/${payload.year}`;

    try {
      const responses = await fetchPerStore(storesToFetch(payload), (store) =>
        getGoalsData(`${base}/${store}?month=${payload.month}`)
      );
      const goals = responses.flatMap((response) => response.goals);
      goals.sort((a, b) => {
        const bVal =
          (b.profit_to_date + b.actual_extra_profit - b.profit_goal) /
          Math.abs(b.profit_goal);
        const aVal =
          (a.profit_to_date + a.actual_extra_profit - a.profit_goal) /
          Math.abs(a.profit_goal);
        return bVal - aVal;
      });
      dispatch(
        getStoreMonthlyBreakdownSuccess({
          data: { year: payload.year, month: payload.month, goals },
          month: payload.month,
        })
      );
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getStoreMonthlyBreakdownFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: base,
          content: message,
        })
      );
    }
  };
}

export function fetchStorePlannings(payload) {
  return async (dispatch) => {
    dispatch(getStorePlannings());

    const base = `${atomicConfig.storeManagementServiceUrl}/fetch-monthly-goals/${payload.year}`;

    try {
      if (payload.store && payload.store !== "all") {
        dispatch(
          getStorePlanningsSuccess(
            await getGoalsData(`${base}/${payload.store}`)
          )
        );
        return;
      }
      // Fan out and sum. Any store failing fails the whole view rather than
      // showing a total that silently leaves that store out.
      const responses = await fetchPerStore(payload.stores || [], (store) =>
        getGoalsData(`${base}/${store}`)
      );
      dispatch(
        getStorePlanningsSuccess(sumMonthlyGoals(responses, payload.year))
      );
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getStorePlanningsFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: base,
          content: message,
        })
      );
    }
  };
}

export function createOrUpdateMonthlyGoal(payload, callback) {
  return async (dispatch) => {
    dispatch(updateMonthlyGoal(payload));
    const store = String(payload.store ?? "").replace("_client", "");
    let url = `${atomicConfig.storeManagementServiceUrl}/create-or-update-monthly-goals/${store}`;
    let json_body = {};

    json_body = JSON.stringify({
      year: payload.year,
      month: payload.month,
      goal: payload.goal,
    });

    try {
      let result = await call_api_auth(url, "POST", json_body);
      result = result.data;

      if (result.message) {
        dispatch(updateMonthlyGoalSuccess());
        callback && callback();
      }
    } catch (result) {
      const message = result || "Something went wrong";
      dispatch(updateMonthlyGoalFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function getWeeklyInsights(payload) {
  return async (dispatch) => {
    dispatch(getWeeklyInsight(payload));
    let url = `${atomicConfig.storeManagementServiceUrl}/weekly-asin-insights/${payload}`;
    try {
      let result = await call_api_auth(url, "GET");
      result = result.data;

      if (result.message !== undefined) {
        dispatch(getWeeklyInsightFailure(result.message));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.message,
          })
        );
      } else {
        dispatch(getWeeklyInsightSuccess(result));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getWeeklyInsightFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function setStore(store) {
  return (dispatch) => {
    dispatch(setStoreSuccess(store));
  };
}

export function setMarketplace(marketplace) {
  return (dispatch) => {
    dispatch(setMarketplaceSuccess(marketplace));
  };
}

// Force Store Analysis to re-dispatch its fetch batch for the current
// store/marketplace. Clears in-flight sales/ranks guards so a refresh isn't
// silently dropped while a prior (or long-held wait_for_fresh) request is open.
export function refreshStoreAnalysis() {
  return (dispatch) => {
    productSalesInFlight.clear();
    productRanksInFlight.clear();
    latestProductSalesUrl = null;
    dispatch(bumpStoreAnalysisRefreshKey());
  };
}

export function fetchActiveNotificationsCount(store) {
  return async (dispatch) => {
    dispatch(getNotificationCount());
    let url;
    url = `${
      atomicConfig.storeManagementServiceUrl
    }/fetch-asin-notifications/${store.replace("_client", "")}`;

    try {
      let result = await call_api_auth(url, "GET");
      //Showing only last week's alerts
      let lastWeekDate = new Date().getTime() - 7 * 24 * 60 * 60 * 1000;
      var lastWeekNotifications = result.data.notifications.filter(
        (item) => new Date(item.create_date).getTime() > lastWeekDate
      );
      var lastWeekDeals = result.data.lightning_deals.filter(
        (item) => item.end_date * 1000 > lastWeekDate
      );
      result = JSON.parse(
        `{"alerts_count": {"${store}": ${
          lastWeekNotifications.length + lastWeekDeals.length
        }}}`
      );
      if (result.message !== undefined) {
        dispatch(getNotificationCountFailure(result.message));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.message,
          })
        );
      } else {
        dispatch(getNotificationCountSuccess(result));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getNotificationCountFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

async function get_store_monthly_goal(store) {
  let url = `${atomicConfig.storeManagementServiceUrl}/current-month-summary-goals/${store}`;
  let result = await call_api_auth(url, "GET");
  // return a promise
  return result;
}

export function fetchRuleEngineDecisions(payload) {
  return async (dispatch, getState) => {
    dispatch(getRuleEngineDecisions());

    const marketplace = payload.marketplace || "us";
    let url = `${atomicConfig.storeManagementServiceUrl}/rule-engine-decisions/${payload.store}?marketplace=${marketplace}`;
    try {
      let result = await call_api_auth(url, "GET");
      if (getState().store_analytics.marketplace !== marketplace) return;
      result = result.data;
      if (result.message !== undefined) {
        dispatch(getRuleEngineDecisionsFailure(result.message));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.message,
          })
        );
      } else {
        dispatch(getRuleEngineDecisionsSuccess(result));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getRuleEngineDecisionsFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function fetchRuleEngineDecisionReasoning(payload) {
  return async (dispatch) => {
    dispatch(getRuleEngineDecisionReasoning());

    let url = `${atomicConfig.storeManagementServiceUrl}/rule-engine-reasoning/${payload.store}`;
    const json_body = JSON.stringify(payload);

    try {
      let result = await call_api_auth(url, "POST", json_body);
      result = result.data;
      if (result.message === undefined) {
        dispatch(getRuleEngineDecisionReasoningFailure(result.message));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.message,
          })
        );
      } else {
        dispatch(
          getRuleEngineDecisionReasoningSuccess({
            asin: payload.asin,
            data: result,
          })
        );
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getRuleEngineDecisionReasoningFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function respondToRuleEngineDecision(payload) {
  return async (dispatch) => {
    dispatch(saveRuleEngineResponse(payload));

    let url = `${atomicConfig.storeManagementServiceUrl}/rule-engine-decisions/${payload.store}?marketplace=${payload.marketplace}`;
    let requestBody = {
      asin: payload.asin,
      sku: payload.sku,
      execution_id: payload.execution_id,
      user_response: payload.user_response,
      target_price: payload.target_price || null,
      selling_price: payload.selling_price || null,
      reject_reason: payload.reject_reason || null,
      accept_reason: payload.accept_reason || null,
      decision_type: payload.decision_type || null,
    };

    const json_body = JSON.stringify(requestBody);

    try {
      let result = await call_api_auth(url, "POST", json_body);
      let status_code = result.status;
      result = result.data;
      if (status_code !== 200) {
        dispatch(
          saveRuleEngineResponseFailure({
            ...result,
            execution_id: payload.execution_id,
            status_key: payload.status_key,
          })
        );
        return { success: false, message: result };
      } else {
        dispatch(
          saveRuleEngineResponseSuccess({
            ...result,
            execution_id: payload.execution_id,
            status_key: payload.status_key,
          })
        );
        return { success: true, data: result };
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(
        saveRuleEngineResponseFailure({
          message: message,
          execution_id: payload.execution_id,
          status_key: payload.status_key,
        })
      );
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
      return { success: false, message };
    }
  };
}

export function acceptFutureDealOption(payload) {
  return async (dispatch) => {
    dispatch(saveRuleEngineResponse(payload));

    const { dealId, store, notes } = payload;
    let url = `${atomicConfig.storeManagementServiceUrl}/deals/${dealId}/decide/${store}`;
    let requestBody = {
      decision: "accepted",
      notes: notes || null,
    };

    const json_body = JSON.stringify(requestBody);

    try {
      let result = await call_api_auth(url, "POST", json_body);
      let status_code = result.status;
      result = result.data;
      if (status_code !== 200) {
        dispatch(
          saveRuleEngineResponseFailure({
            ...result,
            execution_id: payload.execution_id,
            status_key: payload.status_key,
          })
        );
        return {
          success: false,
          message: result.message || "Failed to accept deal",
        };
      } else {
        dispatch(
          saveRuleEngineResponseSuccess({
            ...result,
            execution_id: payload.execution_id,
            status_key: payload.status_key,
          })
        );
        return { success: true, data: result };
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(
        saveRuleEngineResponseFailure({
          message,
          execution_id: payload.execution_id,
          status_key: payload.status_key,
        })
      );
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
      return { success: false, message };
    }
  };
}

export function rejectFutureDealOption(payload) {
  return async (dispatch) => {
    const { dealId, store, notes } = payload;
    let url = `${atomicConfig.storeManagementServiceUrl}/deals/${dealId}/decide/${store}`;
    let requestBody = {
      decision: "rejected",
      notes: notes || null,
    };

    const json_body = JSON.stringify(requestBody);

    try {
      let result = await call_api_auth(url, "POST", json_body);
      let status_code = result.status;
      result = result.data;
      if (status_code !== 200) {
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.message || "Failed to reject deal",
          })
        );
        return {
          success: false,
          message: result.message || "Failed to reject deal",
        };
      } else {
        return { success: true, data: result };
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
      return { success: false, message };
    }
  };
}

export function fetchDealUploadStatus(batchId, store) {
  return async () => {
    let url = `${atomicConfig.storeManagementServiceUrl}/deals/upload-status/${batchId}/${store}`;

    try {
      let result = await call_api_auth(url, "GET");
      let status_code = result.status;
      result = result.data;
      if (status_code !== 200) {
        return {
          success: false,
          message: result.message || "Failed to fetch upload status",
        };
      } else {
        return { success: true, data: result };
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      return { success: false, message };
    }
  };
}

export function fetchActiveInventory(payload) {
  return async (dispatch) => {
    dispatch(getActiveInventory());
    const { store, marketplace } = payload;
    let url = `${
      atomicConfig.storeManagementServiceUrl
    }/active-inventory/${store.replace(
      "_client",
      ""
    )}?marketplace=${marketplace}`;
    try {
      let result = await call_api_auth(url, "GET");
      result = result.data;
      if (result.message !== undefined) {
        dispatch(getActiveInventoryFailure(result.message));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.message,
          })
        );
      } else {
        dispatch(getActiveInventorySuccess(result));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getActiveInventoryFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function fetchPredictedUnits(payload) {
  return async (dispatch) => {
    dispatch(getPredictedUnits());
    const { store, marketplace, months } = payload;
    let url = `${
      atomicConfig.storeManagementServiceUrl
    }/predicted-units/${store.replace(
      "_client",
      ""
    )}?marketplace=${marketplace}&months=${months}`;
    try {
      let result = await call_api_auth(url, "GET");
      result = result.data;
      if (result.message !== undefined) {
        dispatch(getPredictedUnitsFailure(result.message));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.message,
          })
        );
      } else {
        dispatch(getPredictedUnitsSuccess(result));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getPredictedUnitsFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function fetchPastUnitsSales(payload) {
  return async (dispatch) => {
    dispatch(getPastUnitsSales());
    const { store, marketplace } = payload;
    let url = `${
      atomicConfig.storeManagementServiceUrl
    }/past-units-sales/${store.replace(
      "_client",
      ""
    )}?marketplace=${marketplace}`;
    try {
      let result = await call_api_auth(url, "GET");
      result = result.data;
      if (result.message !== undefined) {
        dispatch(getPastUnitsSalesFailure(result.message));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.message,
          })
        );
      } else {
        dispatch(getPastUnitsSalesSuccess(result));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getPastUnitsSalesFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function fetchSellerForecastReviews(payload) {
  return async (dispatch) => {
    dispatch(getSellerForecastReviews());
    const { store, marketplace } = payload;
    let url = `${
      atomicConfig.storeManagementServiceUrl
    }/seller-forecast-reviews/${store.replace(
      "_client",
      ""
    )}?marketplace=${marketplace}`;

    try {
      let result = await call_api_auth(url, "GET");
      result = result.data;
      if (result.message !== undefined) {
        dispatch(
          getSellerForecastReviewsFailure({
            store,
            marketplace,
            message: result.message,
          })
        );
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.message,
          })
        );
      } else {
        dispatch(getSellerForecastReviewsSuccess(result));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getSellerForecastReviewsFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function submitSellerForecastReview(review, store, marketplace) {
  return async (dispatch) => {
    dispatch(saveSellerForecastReview());

    let url = `${
      atomicConfig.storeManagementServiceUrl
    }/seller-forecast-reviews/${store.replace(
      "_client",
      ""
    )}?marketplace=${marketplace}`;
    const json_body = JSON.stringify(review);

    try {
      let result = await call_api_auth(url, "POST", json_body);
      let message = result.data.message || "Review submitted successfully";

      if (result.status !== 200) {
        dispatch(
          saveSellerForecastReviewFailure({
            child_asin: review.child_asin,
            message: message,
          })
        );
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: message,
          })
        );
        return { success: false, message };
      } else {
        dispatch(
          saveSellerForecastReviewSuccess({
            child_asin: review.child_asin,
            message: "Success",
          })
        );
        return { success: true, message };
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(
        saveSellerForecastReviewFailure({
          child_asin: review.child_asin,
          message: message,
        })
      );
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
      return { success: false, message };
    }
  };
}

export function refreshSellerForecastReviewsLocally(payload) {
  return async (dispatch) => {
    dispatch(updateSellerForecastReviewsLocally(payload));
  };
}

export function updateDealUploadStatus(payload) {
  return (dispatch) => {
    dispatch(setDealUploadStatus(payload));
  };
}

export function fetchPpcAnalysis(payload) {
  return async (dispatch) => {
    dispatch(getPpcAnalysis());
    const { store, marketplace } = payload;
    let url = `${
      atomicConfig.storeManagementServiceUrl
    }/ppc-analysis/${store.replace("_client", "")}?marketplace=${marketplace}`;

    try {
      let result = await call_api_auth(url, "GET");
      result = result.data;
      if (result.message !== undefined) {
        dispatch(getPpcAnalysisFailure(result.message));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.message,
          })
        );
      } else {
        dispatch(getPpcAnalysisSuccess(result));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getPpcAnalysisFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

export function clearRuleEngineResponse(payload) {
  return (dispatch) => {
    dispatch(clearRuleEngineResponseStatus(payload));
  };
}

export function fetchRuleEngineDecisionTrace(payload) {
  return async (dispatch) => {
    dispatch(getRuleEngineDecisionTrace());

    const url = `${atomicConfig.storeManagementServiceUrl}/rule-engine-decision-trace/${payload.store}`;
    const json_body = JSON.stringify(payload);

    try {
      let result = await call_api_auth(url, "POST", json_body);
      result = result.data;
      if (!result || !result.decision_trace) {
        dispatch(
          getRuleEngineDecisionTraceFailure("No decision trace data returned")
        );
      } else {
        dispatch(
          getRuleEngineDecisionTraceSuccess({
            asin: payload.asin,
            data: result,
          })
        );
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getRuleEngineDecisionTraceFailure(message));
    }
  };
}

// The docs are the same for every store. The store is in the path only so the
// backend authorizes the request like any other store read.
export function fetchFeatureDocs(store) {
  return async (dispatch, getState) => {
    const { featureDocs } = getState().store_analytics;
    if (Object.keys(featureDocs).length > 0) return; // already loaded
    dispatch(getFeatureDocs());
    const url = `${atomicConfig.storeManagementServiceUrl}/feature-docs/${store}`;
    try {
      let result = await call_api_auth(url, "GET");
      result = result.data;
      if (!result || !result.features) {
        dispatch(getFeatureDocsFailure("No feature docs returned"));
      } else {
        dispatch(getFeatureDocsSuccess(result));
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getFeatureDocsFailure(message));
    }
  };
}

export function fetchPpcAlerts(payload) {
  return async (dispatch) => {
    dispatch(getPpcAlerts());
    const { store, marketplace } = payload;
    const url = `${
      atomicConfig.storeManagementServiceUrl
    }/ppc-alerts/${store.replace("_client", "")}?marketplace=${marketplace}`;

    try {
      let result = await call_api_auth(url, "GET");
      result = result.data;
      if (result.message !== undefined) {
        dispatch(getPpcAlertsFailure(result.message));
        dispatch(
          displayErrorWithTimeout({
            url: url,
            content: result.message,
          })
        );
      } else {
        dispatch(
          getPpcAlertsSuccess(
            Array.isArray(result) ? result : result.alerts ?? result.data ?? []
          )
        );
      }
    } catch (error) {
      const message = error.message || "Something went wrong";
      dispatch(getPpcAlertsFailure(message));
      dispatch(
        displayErrorWithTimeout({
          url: url,
          content: message,
        })
      );
    }
  };
}

// Bumped by every ads-health fetch, so a slower superseded response cannot
// overwrite the store or marketplace the user has since switched to.
let adsHealthRequest = 0;

// Per-store engine runs can take tens of seconds; a few at a time keeps a
// large fan-out from piling onto the database.
const ADS_HEALTH_CONCURRENCY = 4;

const adsHealthUrl = (store, marketplace) =>
  `${atomicConfig.storeManagementServiceUrl}/ads-health/${store}` +
  `?marketplace=${marketplace}`;

export function fetchAdsHealthAlerts(payload) {
  return async (dispatch) => {
    const request = ++adsHealthRequest;
    dispatch(getAdsHealthAlerts());
    dispatch(setAdsHealthProgress(null));
    const { store, marketplace } = payload;
    const cleanStore = store.replace("_client", "");
    try {
      const result = await call_api_auth(
        adsHealthUrl(cleanStore, marketplace),
        "GET"
      );
      if (request !== adsHealthRequest) return;
      dispatch(getAdsHealthAlertsSuccess(result.data));
    } catch (error) {
      if (request !== adsHealthRequest) return;
      dispatch(
        getAdsHealthAlertsFailure(error.message || "Failed to load alerts")
      );
    }
  };
}

// All-stores alerts built from one request per store. Results render as each
// store arrives; stores that fail are reported rather than dropped silently.
export function fetchAdsHealthAlertsAcrossStores({ stores, marketplace }) {
  return async (dispatch) => {
    const request = ++adsHealthRequest;
    const total = stores.length;
    const loaded = [];
    const failedStores = [];
    dispatch(getAdsHealthAlerts());
    dispatch(setAdsHealthProgress({ done: 0, total, failedStores: [] }));

    await forEachWithConcurrency(
      stores,
      ADS_HEALTH_CONCURRENCY,
      async (store) => {
        try {
          const result = await call_api_auth(
            adsHealthUrl(store, marketplace),
            "GET"
          );
          loaded.push({ store, body: result.data });
        } catch {
          failedStores.push(store);
        }
        if (request !== adsHealthRequest) return;
        dispatch(
          setAdsHealthProgress({
            done: loaded.length + failedStores.length,
            total,
            failedStores: [...failedStores],
          })
        );
        if (loaded.length > 0) {
          dispatch(getAdsHealthAlertsSuccess(mergeStoreAlerts(loaded)));
        }
      }
    );

    if (request !== adsHealthRequest) return;
    if (total === 0) {
      dispatch(getAdsHealthAlertsSuccess(emptyAlertsPayload()));
    } else if (loaded.length === 0) {
      dispatch(
        getAdsHealthAlertsFailure(
          `Could not load alerts for any of the ${total} stores`
        )
      );
    }
  };
}

//not in use
// export function removeDealUploadStatus(payload) {
//   return (dispatch) => {
//     dispatch(clearDealUploadStatus(payload));
//   };
// }
